import { useQuery } from '@tanstack/react-query'
import { useOperationEnvironment } from 'contexts/OperationEnvironmentContext'
import {
  OperationCustomer,
  OperationMeter,
  OperationProduct,
  OperationProvider,
  OperationProviderUser,
  OperationPurchase,
  OperationTransaction,
  ProviderListOptions,
  ProviderUsage,
} from 'types/operation'
import { operationKeys } from '../keys'
import { QueryOpts } from '../shared'
import { opGet, opSearchParams, withEnv } from './shared'

const BASE = '/ui/operation/providers'

export const useOperationProvidersQuery = (
  options?: ProviderListOptions,
  queryOptions?: QueryOpts<OperationProvider[], ReturnType<typeof operationKeys.providers.list>>,
) => {
  const { environment } = useOperationEnvironment()
  return useQuery({
    queryKey: operationKeys.providers.list(environment, options),
    queryFn: async () => {
      const params = withEnv(
        environment,
        opSearchParams({
          search: options?.search,
          status: options?.status,
          limit: options?.limit ?? 200,
          offset: options?.offset,
        }),
      )
      const data = await opGet<{ providers: OperationProvider[] }>(BASE, params)
      return data.providers ?? []
    },
    placeholderData: previous => previous,
    ...queryOptions,
  })
}

export const useOperationProviderQuery = (
  id?: string,
  queryOptions?: QueryOpts<
    OperationProvider | null,
    ReturnType<typeof operationKeys.providers.detail>
  >,
) => {
  const { environment } = useOperationEnvironment()
  const { enabled, ...rest } = queryOptions ?? {}
  return useQuery({
    queryKey: operationKeys.providers.detail(environment, id ?? 'unknown'),
    queryFn: async () => {
      const data = await opGet<{ provider: OperationProvider | null }>(
        `${BASE}/${id}`,
        withEnv(environment),
      )
      return data.provider ?? null
    },
    enabled: Boolean(id) && (enabled ?? true),
    ...rest,
  })
}

export const useProviderProductsQuery = (
  id?: string,
  queryOptions?: QueryOpts<OperationProduct[], ReturnType<typeof operationKeys.providers.products>>,
) => {
  const { environment } = useOperationEnvironment()
  const { enabled, ...rest } = queryOptions ?? {}
  return useQuery({
    queryKey: operationKeys.providers.products(environment, id ?? 'unknown'),
    queryFn: async () => {
      const data = await opGet<{ items: OperationProduct[] }>(
        `${BASE}/${id}/products`,
        withEnv(environment, opSearchParams({ limit: 200 })),
      )
      return data.items ?? []
    },
    enabled: Boolean(id) && (enabled ?? true),
    ...rest,
  })
}

export const useProviderPurchasesQuery = (
  id?: string,
  queryOptions?: QueryOpts<
    OperationPurchase[],
    ReturnType<typeof operationKeys.providers.purchases>
  >,
) => {
  const { environment } = useOperationEnvironment()
  const { enabled, ...rest } = queryOptions ?? {}
  return useQuery({
    queryKey: operationKeys.providers.purchases(environment, id ?? 'unknown'),
    queryFn: async () => {
      const data = await opGet<{ purchases: OperationPurchase[] }>(
        `${BASE}/${id}/purchases`,
        withEnv(environment),
      )
      return data.purchases ?? []
    },
    enabled: Boolean(id) && (enabled ?? true),
    ...rest,
  })
}

export const useProviderTransactionsQuery = (
  id?: string,
  queryOptions?: QueryOpts<
    OperationTransaction[],
    ReturnType<typeof operationKeys.providers.transactions>
  >,
) => {
  const { environment } = useOperationEnvironment()
  const { enabled, ...rest } = queryOptions ?? {}
  return useQuery({
    queryKey: operationKeys.providers.transactions(environment, id ?? 'unknown'),
    queryFn: async () => {
      const data = await opGet<{ transactions: OperationTransaction[] }>(
        `${BASE}/${id}/transactions`,
        withEnv(environment, opSearchParams({ limit: 200 })),
      )
      return data.transactions ?? []
    },
    enabled: Boolean(id) && (enabled ?? true),
    ...rest,
  })
}

/**
 * Purchases for a single customer within a provider. Filtered server-side by
 * `customerId` (the drawer must not over-fetch the provider's whole set and
 * slice it client-side).
 */
export const useCustomerPurchasesQuery = (
  providerId?: string,
  customerId?: string,
  queryOptions?: QueryOpts<
    OperationPurchase[],
    ReturnType<typeof operationKeys.providers.customerPurchases>
  >,
) => {
  const { environment } = useOperationEnvironment()
  const { enabled, ...rest } = queryOptions ?? {}
  return useQuery({
    queryKey: operationKeys.providers.customerPurchases(
      environment,
      providerId ?? 'unknown',
      customerId ?? 'unknown',
    ),
    queryFn: async () => {
      const data = await opGet<{ purchases: OperationPurchase[] }>(
        `${BASE}/${providerId}/purchases`,
        withEnv(environment, opSearchParams({ customerId, limit: 200 })),
      )
      return data.purchases ?? []
    },
    enabled: Boolean(providerId) && Boolean(customerId) && (enabled ?? true),
    ...rest,
  })
}

/**
 * Transactions for a single customer within a provider. Transactions have no
 * `customerId` field — the backend resolves the link via from/to entity ids —
 * so this MUST be filtered server-side rather than pulling a capped page and
 * filtering on a field that doesn't exist.
 */
export const useCustomerTransactionsQuery = (
  providerId?: string,
  customerId?: string,
  queryOptions?: QueryOpts<
    OperationTransaction[],
    ReturnType<typeof operationKeys.providers.customerTransactions>
  >,
) => {
  const { environment } = useOperationEnvironment()
  const { enabled, ...rest } = queryOptions ?? {}
  return useQuery({
    queryKey: operationKeys.providers.customerTransactions(
      environment,
      providerId ?? 'unknown',
      customerId ?? 'unknown',
    ),
    queryFn: async () => {
      const data = await opGet<{ transactions: OperationTransaction[] }>(
        `${BASE}/${providerId}/transactions`,
        withEnv(environment, opSearchParams({ customerId, limit: 200 })),
      )
      return data.transactions ?? []
    },
    enabled: Boolean(providerId) && Boolean(customerId) && (enabled ?? true),
    ...rest,
  })
}

export const useProviderCustomersQuery = (
  id?: string,
  queryOptions?: QueryOpts<
    OperationCustomer[],
    ReturnType<typeof operationKeys.providers.customers>
  >,
) => {
  const { environment } = useOperationEnvironment()
  const { enabled, ...rest } = queryOptions ?? {}
  return useQuery({
    queryKey: operationKeys.providers.customers(environment, id ?? 'unknown'),
    queryFn: async () => {
      const data = await opGet<{ items: OperationCustomer[] }>(
        `${BASE}/${id}/customers`,
        withEnv(environment, opSearchParams({ limit: 200 })),
      )
      return data.items ?? []
    },
    enabled: Boolean(id) && (enabled ?? true),
    ...rest,
  })
}

export const useProviderUsersQuery = (
  id?: string,
  queryOptions?: QueryOpts<
    OperationProviderUser[],
    ReturnType<typeof operationKeys.providers.users>
  >,
) => {
  const { environment } = useOperationEnvironment()
  const { enabled, ...rest } = queryOptions ?? {}
  return useQuery({
    queryKey: operationKeys.providers.users(environment, id ?? 'unknown'),
    queryFn: async () => {
      const data = await opGet<{ users: OperationProviderUser[] }>(
        `${BASE}/${id}/users`,
        withEnv(environment),
      )
      return data.users ?? []
    },
    enabled: Boolean(id) && (enabled ?? true),
    ...rest,
  })
}

export const useProviderMetersQuery = (
  id?: string,
  queryOptions?: QueryOpts<OperationMeter[], ReturnType<typeof operationKeys.providers.meters>>,
) => {
  const { environment } = useOperationEnvironment()
  const { enabled, ...rest } = queryOptions ?? {}
  return useQuery({
    queryKey: operationKeys.providers.meters(environment, id ?? 'unknown'),
    queryFn: async () => {
      const data = await opGet<{ meters: OperationMeter[] }>(
        `${BASE}/${id}/meters`,
        withEnv(environment),
      )
      return data.meters ?? []
    },
    enabled: Boolean(id) && (enabled ?? true),
    ...rest,
  })
}

export const useProviderUsageQuery = (
  id: string | undefined,
  from: string,
  to: string,
  queryOptions?: QueryOpts<ProviderUsage, ReturnType<typeof operationKeys.providers.usage>>,
) => {
  const { environment } = useOperationEnvironment()
  const { enabled, ...rest } = queryOptions ?? {}
  return useQuery({
    queryKey: operationKeys.providers.usage(environment, id ?? 'unknown', from, to),
    queryFn: () =>
      opGet<ProviderUsage>(
        `${BASE}/${id}/usage`,
        withEnv(environment, opSearchParams({ from, to })),
      ),
    enabled: Boolean(id) && (enabled ?? true),
    ...rest,
  })
}

// ===== Single-entity detail reads (deep-linkable detail pages) =====
// These resolve one document by id (env-scoped) so a detail page survives a
// refresh / direct URL rather than depending on list-query cache.

export const useOperationProductQuery = (
  productId?: string,
  queryOptions?: QueryOpts<
    OperationProduct | null,
    ReturnType<typeof operationKeys.entities.product>
  >,
) => {
  const { environment } = useOperationEnvironment()
  const { enabled, ...rest } = queryOptions ?? {}
  return useQuery({
    queryKey: operationKeys.entities.product(environment, productId ?? 'unknown'),
    queryFn: () => opGet<OperationProduct>(`${BASE}/products/${productId}`, withEnv(environment)),
    enabled: Boolean(productId) && (enabled ?? true),
    ...rest,
  })
}

export const useOperationCustomerQuery = (
  customerId?: string,
  queryOptions?: QueryOpts<
    OperationCustomer | null,
    ReturnType<typeof operationKeys.entities.customer>
  >,
) => {
  const { environment } = useOperationEnvironment()
  const { enabled, ...rest } = queryOptions ?? {}
  return useQuery({
    queryKey: operationKeys.entities.customer(environment, customerId ?? 'unknown'),
    queryFn: () =>
      opGet<OperationCustomer>(`${BASE}/customers/${customerId}`, withEnv(environment)),
    enabled: Boolean(customerId) && (enabled ?? true),
    ...rest,
  })
}

export const useOperationPurchaseQuery = (
  purchaseId?: string,
  queryOptions?: QueryOpts<
    OperationPurchase | null,
    ReturnType<typeof operationKeys.entities.purchase>
  >,
) => {
  const { environment } = useOperationEnvironment()
  const { enabled, ...rest } = queryOptions ?? {}
  return useQuery({
    queryKey: operationKeys.entities.purchase(environment, purchaseId ?? 'unknown'),
    queryFn: () =>
      opGet<OperationPurchase>(`${BASE}/purchases/${purchaseId}`, withEnv(environment)),
    enabled: Boolean(purchaseId) && (enabled ?? true),
    ...rest,
  })
}

export const useOperationMeterQuery = (
  meterId?: string,
  queryOptions?: QueryOpts<OperationMeter | null, ReturnType<typeof operationKeys.entities.meter>>,
) => {
  const { environment } = useOperationEnvironment()
  const { enabled, ...rest } = queryOptions ?? {}
  return useQuery({
    queryKey: operationKeys.entities.meter(environment, meterId ?? 'unknown'),
    queryFn: () => opGet<OperationMeter>(`${BASE}/meters/${meterId}`, withEnv(environment)),
    enabled: Boolean(meterId) && (enabled ?? true),
    ...rest,
  })
}

export const useOperationTransactionQuery = (
  transactionId?: string,
  queryOptions?: QueryOpts<
    OperationTransaction | null,
    ReturnType<typeof operationKeys.entities.transaction>
  >,
) => {
  const { environment } = useOperationEnvironment()
  const { enabled, ...rest } = queryOptions ?? {}
  return useQuery({
    queryKey: operationKeys.entities.transaction(environment, transactionId ?? 'unknown'),
    queryFn: () =>
      opGet<OperationTransaction>(`${BASE}/transactions/${transactionId}`, withEnv(environment)),
    enabled: Boolean(transactionId) && (enabled ?? true),
    ...rest,
  })
}
